import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const SignupMessage = styled.div`
  font-size: 1rem;
  font-weight: 900;
  color: #323232;
  text-align: center;
  margin-top: 2rem;
`;

const GenerateButton = styled.button`
  margin-top: 15px;
  padding: 10px 20px;
  background: #009f6f;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  font-weight: bold;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const Countdown = styled.div`
  margin-top: 0.75rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
`;

/** "04:57:30 UTC" — the hold always ends on a UTC wall clock, so render it as one. */
function formatUtcTime(ms: number): string {
  return `${new Date(ms).toISOString().slice(11, 19)} UTC`;
}

/** "4h 12m 03s" / "12m 03s" / "43s" */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`;
  if (m > 0) return `${m}m ${pad(s)}s`;
  return `${s}s`;
}

/**
 * Birthday-priority window screen.
 *
 * `unlockAt` comes from the server (`scheduledTime + 450s`, where
 * `scheduledTime` is 04:50 UTC shifted by the day's `utcOffset`). It is NOT a
 * fixed hour: on a `utcOffset: 6` day the hold runs until 10:57:30 UTC. This
 * screen used to hardcode 09:40 UTC (from the old 4h50m hold), so on every
 * shifted day it told everyone "the priority period has ended" hours before
 * the backend would actually let them claim — the Generate button then
 * re-fired `/today`, got the same 269 veto back, and looked inert.
 */
export const BirthdayPriorityState: React.FC<{
  birthdayUsers: string[];
  /** ISO timestamp when the hold lifts. Absent only on legacy payloads. */
  unlockAt?: string;
  onRetry: () => void;
}> = ({ birthdayUsers, unlockAt, onRetry }) => {
  const unlockMs = unlockAt ? Date.parse(unlockAt) : NaN;
  const hasUnlock = Number.isFinite(unlockMs);

  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!hasUnlock) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasUnlock]);

  // Unknown unlock time (legacy payload): don't invent one — leave the user on
  // the priority screen but keep the manual retry available so they're never
  // stuck behind a stale client-side guess.
  const windowOpen = hasUnlock ? now >= unlockMs : true;

  // Self-heal the moment the hold lifts, so nobody has to sit there clicking —
  // but ONLY on a boundary crossing observed while mounted. If the window
  // already reads open on first render (a client clock running ahead of the
  // server, which is still vetoing), auto-firing would bounce through
  // loading → 269 → remount → fire again, forever. Those users get the button.
  const autoRetryDoneRef = useRef<boolean | null>(null);
  if (autoRetryDoneRef.current === null) autoRetryDoneRef.current = windowOpen;

  useEffect(() => {
    if (!hasUnlock || !windowOpen || autoRetryDoneRef.current) return;
    autoRetryDoneRef.current = true;
    onRetry();
  }, [hasUnlock, windowOpen, onRetry]);

  const celebrants = (
    <div style={{ marginBottom: "1rem" }}>
      🎉 User{birthdayUsers.length > 1 ? "s" : ""} {birthdayUsers.join(", ")}{" "}
      {birthdayUsers.length > 1 ? "have" : "has"} a birthday today! 🎉
    </div>
  );

  return (
    <SignupMessage
      style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}
    >
      {!windowOpen ? (
        <>
          {celebrants}
          <div>
            They have priority to generate the art until {formatUtcTime(unlockMs)}. You can generate
            today&apos;s art if they haven&apos;t by then.
          </div>
          <Countdown>Opens for everyone in {formatRemaining(unlockMs - now)}</Countdown>
        </>
      ) : hasUnlock ? (
        <div>
          The birthday priority period has ended. You can now try generating the art!
          <GenerateButton type="button" onClick={onRetry}>
            Generate Art
          </GenerateButton>
        </div>
      ) : (
        <>
          {celebrants}
          <div>They have priority to generate today&apos;s art first.</div>
          <GenerateButton type="button" onClick={onRetry}>
            Try generating
          </GenerateButton>
        </>
      )}
    </SignupMessage>
  );
};

export default BirthdayPriorityState;
